import { Auth } from '@aws-amplify/auth'
import { sendHttpRequest } from '@/ai/api/httpClient.js'

export const runTests = async (options = {}, onChunk = null) => {
  const RUN_TESTS_API_URL = process.env.VUE_APP_RUN_TESTS_API_URL

  if (!RUN_TESTS_API_URL) {
    throw new Error('Run Tests API URL is not configured')
  }

  let jwtToken
  try {
    const currentUser = await Auth.currentAuthenticatedUser()
    jwtToken = currentUser.signInUserSession.idToken.jwtToken
  } catch (authError) {
    throw new Error('User authentication failed. Cannot call run tests service.')
  }

  const requestBody = {
    // Add any options here in the future
    ...options
  }

  const requestHeaders = {
    Authorization: `Bearer ${jwtToken}`,
    'Content-Type': 'text/plain;charset=UTF-8'
  }

  try {
    const response = await sendHttpRequest(
      RUN_TESTS_API_URL,
      'POST',
      requestHeaders,
      JSON.stringify(requestBody)
    )

    // Handle streaming response
    if (!response.body) {
      throw new Error('No response body received')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    const results = []

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk

        // Process complete lines
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim()) {
            try {
              const parsed = JSON.parse(line)
              results.push(parsed)

              // Call the streaming callback if provided
              if (onChunk && typeof onChunk === 'function') {
                onChunk(parsed)
              }
            } catch (parseError) {
              // If it's not JSON, treat as raw output
              const rawResult = {
                type: 'output',
                message: line
              }
              results.push(rawResult)

              if (onChunk && typeof onChunk === 'function') {
                onChunk(rawResult)
              }
            }
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer)
          results.push(parsed)
          if (onChunk && typeof onChunk === 'function') {
            onChunk(parsed)
          }
        } catch (parseError) {
          const rawResult = {
            type: 'output',
            message: buffer
          }
          results.push(rawResult)
          if (onChunk && typeof onChunk === 'function') {
            onChunk(rawResult)
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    return {
      success: true,
      results,
      streaming: true
    }
  } catch (error) {
    console.error('Run Tests API Error:', error)
    throw error
  }
}
